import { useMemo } from 'react';
import { useTexture } from '@react-three/drei';
import { DoubleSide, RepeatWrapping, SRGBColorSpace } from 'three';
import { FIELD_LENGTH, FIELD_WIDTH } from './formationProjection';

const PITCH_DEEP = '#03130a';
const GRASS_TEX_URL = '/pitch_grass.png';
// Tile the 512² grass texture more densely so the underlying detail
// reads finer and the repeat is less obvious.
const GRASS_TILE_X = FIELD_WIDTH / 6;
const GRASS_TILE_Z = FIELD_LENGTH / 6;

// Painted markings sit a hair above the grass to avoid z-fighting,
// and below all player tokens (y=0.6+) so figures cast shadows on them.
const LINE_Y = 0.006;
const LINE_COLOR = '#f0f3ed';
// 12cm-wide painted line — matches FIFA regulation.
const LINE_WIDTH = 0.12;

// Regulation dimensions (metres) — kept as constants so they're greppable
// against the laws of the game.
const PENALTY_AREA_WIDTH = 40.32;
const PENALTY_AREA_DEPTH = 16.5;
const GOAL_AREA_WIDTH = 18.32;
const GOAL_AREA_DEPTH = 5.5;
const PENALTY_SPOT_FROM_GOAL = 11;
const CENTRE_CIRCLE_RADIUS = 9.15;
const PENALTY_ARC_RADIUS = 9.15;
const CORNER_ARC_RADIUS = 1;

// Block raycasts on every line mesh — otherwise clicking on a painted
// line would consume the event instead of falling through to the
// empty-pitch click target that clears selection. Lines are decorative
// only; they should never be hit-testable.
const noRaycast = () => undefined;

interface LineSegProps {
  /** World-space start point on the ground plane (x, z in metres). */
  from: [number, number];
  /** World-space end point on the ground plane (x, z in metres). */
  to: [number, number];
}

function LineSeg({ from, to }: LineSegProps) {
  const dx = to[0] - from[0];
  const dz = to[1] - from[1];
  const length = Math.hypot(dx, dz);
  // atan2(dz, dx) gives the angle of the line in the world XZ plane.
  // We rotate around Y to align the plane's X axis along that direction;
  // the plane was first laid flat via the X-axis rotation.
  const angle = Math.atan2(dz, dx);
  const cx = (from[0] + to[0]) / 2;
  const cz = (from[1] + to[1]) / 2;
  return (
    <mesh
      position={[cx, LINE_Y, cz]}
      rotation={[-Math.PI / 2, 0, -angle]}
      raycast={noRaycast}
      receiveShadow
    >
      <planeGeometry args={[length, LINE_WIDTH]} />
      <meshStandardMaterial color={LINE_COLOR} roughness={0.7} metalness={0} />
    </mesh>
  );
}

interface RectOutlineProps {
  cx: number;
  cz: number;
  w: number;
  h: number;
}

function RectOutline({ cx, cz, w, h }: RectOutlineProps) {
  const x0 = cx - w / 2;
  const x1 = cx + w / 2;
  const z0 = cz - h / 2;
  const z1 = cz + h / 2;
  return (
    <>
      <LineSeg from={[x0, z0]} to={[x1, z0]} />
      <LineSeg from={[x1, z0]} to={[x1, z1]} />
      <LineSeg from={[x1, z1]} to={[x0, z1]} />
      <LineSeg from={[x0, z1]} to={[x0, z0]} />
    </>
  );
}

interface ArcProps {
  cx: number;
  cz: number;
  radius: number;
  /** Local-frame start angle (radians). theta=0 points along world +X;
   *  theta=π/2 points along world −Z (because the ring is rotated [-π/2,0,0]). */
  thetaStart?: number;
  thetaLength?: number;
}

function Arc({ cx, cz, radius, thetaStart = 0, thetaLength = Math.PI * 2 }: ArcProps) {
  return (
    <mesh
      position={[cx, LINE_Y, cz]}
      rotation={[-Math.PI / 2, 0, 0]}
      raycast={noRaycast}
      receiveShadow
    >
      <ringGeometry
        args={[
          radius - LINE_WIDTH / 2,
          radius + LINE_WIDTH / 2,
          64,
          1,
          thetaStart,
          thetaLength,
        ]}
      />
      <meshStandardMaterial
        color={LINE_COLOR}
        roughness={0.7}
        metalness={0}
        side={DoubleSide}
      />
    </mesh>
  );
}

interface SpotProps {
  cx: number;
  cz: number;
  radius?: number;
}

function Spot({ cx, cz, radius = 0.18 }: SpotProps) {
  return (
    <mesh
      position={[cx, LINE_Y, cz]}
      rotation={[-Math.PI / 2, 0, 0]}
      raycast={noRaycast}
      receiveShadow
    >
      <circleGeometry args={[radius, 24]} />
      <meshStandardMaterial color={LINE_COLOR} roughness={0.7} metalness={0} />
    </mesh>
  );
}

/**
 * Painted regulation markings — touchlines, halfway, centre circle +
 * spot, both penalty + goal areas, penalty spots and D-arcs, corner
 * arcs. Rendered as thin white planes / rings just above the grass.
 * Without this, the pitch reads as "a green rug" — the markings are
 * what make it legible as a football pitch.
 */
function FieldLines() {
  const halfL = FIELD_LENGTH / 2;
  const halfW = FIELD_WIDTH / 2;
  // Penalty spot is 11m from the goal line; D arc is centred on the spot,
  // which sits 5.5m outside the penalty area edge.
  const penaltySpotZ = halfL - PENALTY_SPOT_FROM_GOAL;
  const distSpotToPAEdge = PENALTY_AREA_DEPTH - PENALTY_SPOT_FROM_GOAL;
  // Half-angle of the visible portion of the D arc, derived from
  // chord-radius geometry on the penalty area edge.
  const dHalfAngle = Math.acos(distSpotToPAEdge / PENALTY_ARC_RADIUS);

  return (
    <group>
      {/* Outer touchlines + goal lines */}
      <RectOutline cx={0} cz={0} w={FIELD_WIDTH} h={FIELD_LENGTH} />
      {/* Halfway line */}
      <LineSeg from={[-halfW, 0]} to={[halfW, 0]} />
      {/* Centre circle + centre spot */}
      <Arc cx={0} cz={0} radius={CENTRE_CIRCLE_RADIUS} />
      <Spot cx={0} cz={0} />

      {/* Home end (positive z) */}
      <RectOutline
        cx={0}
        cz={halfL - PENALTY_AREA_DEPTH / 2}
        w={PENALTY_AREA_WIDTH}
        h={PENALTY_AREA_DEPTH}
      />
      <RectOutline
        cx={0}
        cz={halfL - GOAL_AREA_DEPTH / 2}
        w={GOAL_AREA_WIDTH}
        h={GOAL_AREA_DEPTH}
      />
      <Spot cx={0} cz={penaltySpotZ} />
      {/* D arc opens toward halfway, i.e. world -Z = local +Y, theta=π/2. */}
      <Arc
        cx={0}
        cz={penaltySpotZ}
        radius={PENALTY_ARC_RADIUS}
        thetaStart={Math.PI / 2 - dHalfAngle}
        thetaLength={2 * dHalfAngle}
      />

      {/* Away end (negative z) — mirrors home */}
      <RectOutline
        cx={0}
        cz={-(halfL - PENALTY_AREA_DEPTH / 2)}
        w={PENALTY_AREA_WIDTH}
        h={PENALTY_AREA_DEPTH}
      />
      <RectOutline
        cx={0}
        cz={-(halfL - GOAL_AREA_DEPTH / 2)}
        w={GOAL_AREA_WIDTH}
        h={GOAL_AREA_DEPTH}
      />
      <Spot cx={0} cz={-penaltySpotZ} />
      {/* D arc opens toward halfway, i.e. world +Z = local -Y, theta=-π/2. */}
      <Arc
        cx={0}
        cz={-penaltySpotZ}
        radius={PENALTY_ARC_RADIUS}
        thetaStart={-Math.PI / 2 - dHalfAngle}
        thetaLength={2 * dHalfAngle}
      />

      {/* Corner arcs — each is a quarter-circle opening into the field.
          Local theta=0 points world +X, theta=π/2 points world -Z. */}
      <Arc cx={-halfW} cz={halfL} radius={CORNER_ARC_RADIUS} thetaStart={0} thetaLength={Math.PI / 2} />
      <Arc cx={halfW} cz={halfL} radius={CORNER_ARC_RADIUS} thetaStart={Math.PI / 2} thetaLength={Math.PI / 2} />
      <Arc cx={halfW} cz={-halfL} radius={CORNER_ARC_RADIUS} thetaStart={Math.PI} thetaLength={Math.PI / 2} />
      <Arc cx={-halfW} cz={-halfL} radius={CORNER_ARC_RADIUS} thetaStart={-Math.PI / 2} thetaLength={Math.PI / 2} />
    </group>
  );
}

export function FieldMesh() {
  const grass = useTexture(GRASS_TEX_URL);
  // Configure the grass texture once per resource load.
  useMemo(() => {
    grass.wrapS = RepeatWrapping;
    grass.wrapT = RepeatWrapping;
    grass.repeat.set(GRASS_TILE_X, GRASS_TILE_Z);
    grass.colorSpace = SRGBColorSpace;
    grass.anisotropy = 8;
    grass.needsUpdate = true;
  }, [grass]);

  return (
    <group>
      {/* Shadow surround — a touch larger than the pitch so shadows from
          tokens near the touchline don't fall off a hard edge. */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, -0.01, 0]}
        receiveShadow
      >
        <planeGeometry args={[FIELD_WIDTH * 1.4, FIELD_LENGTH * 1.4]} />
        <meshStandardMaterial color={PITCH_DEEP} roughness={1} />
      </mesh>
      {/* Pitch surface — grass texture tinted with a richer green base.
          Roughness dropped from 0.92 to 0.78 so the directional rig + HDRI
          give the surface subtle highlight variation rather than reading
          as a uniform matte rectangle. */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, 0, 0]}
        receiveShadow
      >
        <planeGeometry args={[FIELD_WIDTH, FIELD_LENGTH]} />
        <meshStandardMaterial
          map={grass}
          color="#4f8a52"
          roughness={0.78}
          metalness={0}
        />
      </mesh>
      <FieldLines />
    </group>
  );
}
